import { useState, useRef } from "react";

/**
 * CC 智能实验员专属状态 Hook
 *
 * 管理 CC Bridge 交互所需的全部状态：
 * - 设备选择（ccSelectedDevice）
 * - 消息列表（ccMessages）
 * - 会话状态（ccSessionState）
 * - 权限审批（ccPendingPermission）
 * - 任务信息（ccTaskInfo）
 */
const useCCState = () => {
  // 学生选中的 CC 实验员设备信息 { device_id, device_name, device_password }
  const [ccSelectedDevice, setCCSelectedDevice] = useState(null);

  // CC 消息列表，每条消息格式：
  // { id, type, content, toolName, toolInput, toolResult, timestamp, isStreaming }
  // type: "text" | "tool_use" | "tool_result" | "terminal" | "result" | "status" | "tool_progress"
  const [ccMessages, setCCMessages] = useState([]);

  // CC 会话状态：idle / running / requires_action
  const [ccSessionState, setCCSessionState] = useState("idle");

  // 顶部状态条文本
  const [ccStatus, setCCStatus] = useState("");

  // 待审批的权限请求 { request_id, tool_name, tool_input, description }
  const [ccPendingPermission, setCCPendingPermission] = useState(null);

  // AskUserQuestion 待回答的问题 { tool_use_id, questions: [{question, header, options:[{label,description}], multiSelect}] }
  const [ccPendingQuestion, setCCPendingQuestion] = useState(null);

  // 当前任务信息 { task_id, cost_usd, duration_ms, is_error, error_msg }
  const [ccTaskInfo, setCCTaskInfo] = useState(null);

  // CC 会话初始化信息（来自 system/init，同一 session 不变）
  // { cwd, permissionMode }
  const [ccInitInfo, setCCInitInfo] = useState(null);

  // 本次会话 token 累积用量（实时更新）
  // { input, output, cacheRead, turns }
  const [ccTokenUsage, setCCTokenUsage] = useState({ input: 0, output: 0, cacheRead: 0, turns: 0 });

  // 当前流式文本（stream_event 累积），用于打字机效果
  const [ccStreamingText, setCCStreamingText] = useState("");
  // ref 镜像：同步跟踪 ccStreamingText 的最新值，供固化逻辑直接读取，避免闭包捕获旧值
  const ccStreamingTextRef = useRef("");

  // 当前流式思考文本（thinking_delta 累积）
  const [ccThinkingText, setCCThinkingText] = useState("");

  // 文件树：{ [filepath]: { size, modified } }（来自 system.files_persisted）
  const [ccFileTree, setCCFileTree] = useState({});

  // 研讨角色名册（来自 youlaber_roster 事件）：[{ name, description, role }]
  // 供认知协同界面标注"是哪个子智能体在发言"，以及顶部角色名册条展示。
  const [ccRoster, setCCRoster] = useState([]);

  const resetCCState = () => {
    setCCMessages([]);
    setCCSessionState("idle");
    setCCStatus("");
    setCCPendingPermission(null);
    setCCPendingQuestion(null);
    setCCTaskInfo(null);
    setCCTokenUsage({ input: 0, output: 0, cacheRead: 0, turns: 0 });
    ccStreamingTextRef.current = "";
    setCCStreamingText("");
    setCCThinkingText("");
    setCCFileTree({});
    setCCRoster([]);
  };

  return {
    ccSelectedDevice,
    setCCSelectedDevice,
    ccMessages,
    setCCMessages,
    ccSessionState,
    setCCSessionState,
    ccStatus,
    setCCStatus,
    ccPendingPermission,
    setCCPendingPermission,
    ccPendingQuestion,
    setCCPendingQuestion,
    ccTaskInfo,
    setCCTaskInfo,
    ccInitInfo,
    setCCInitInfo,
    ccTokenUsage,
    setCCTokenUsage,
    ccStreamingText,
    setCCStreamingText,
    ccStreamingTextRef,
    ccThinkingText,
    setCCThinkingText,
    ccFileTree,
    setCCFileTree,
    ccRoster,
    setCCRoster,
    resetCCState,
  };
};

export default useCCState;
